<template>
  <Teleport to="body">
    <transition name="picker-fade">
      <div v-if="visible" class="emoji-picker-overlay" @click.self="close">
        <transition name="picker-slide">
          <div v-if="visible" class="emoji-picker-sheet" ref="sheet">
            <!-- Header -->
            <div class="picker-header">
              <div class="picker-handle"></div>
              <div class="picker-title">
                {{ $t('chat.choose_reaction') || 'Choose Reaction' }}
              </div>
              <button class="picker-close" @click="close">
                <i class="bi bi-x-lg"></i>
              </button>
            </div>
            
            <!-- Quick Reactions (Flutter sync: common emojis at top) -->
            <div class="quick-reactions">
              <button
                v-for="emoji in quickEmojis"
                :key="emoji"
                class="quick-emoji"
                :class="{ 'is-reacted': userReactedEmojis.has(emoji) }"
                @click="selectEmoji(emoji)"
              >
                {{ emoji }}
              </button>
            </div>
            
            <!-- Category Tabs (Flutter sync: TabBar) -->
            <div class="category-tabs">
              <button
                v-for="(cat, index) in categories"
                :key="cat.icon"
                class="category-tab"
                :class="{ active: activeCategory === index }"
                @click="activeCategory = index"
              >
                {{ cat.icon }}
              </button>
            </div>
            
            <!-- Emoji Grid -->
            <div class="emoji-grid-container" ref="gridContainer">
              <div class="emoji-grid">
                <button
                  v-for="emoji in currentEmojis"
                  :key="emoji"
                  class="emoji-item"
                  :class="{ 'is-reacted': userReactedEmojis.has(emoji) }"
                  @click="selectEmoji(emoji)"
                >
                  {{ emoji }}
                </button>
              </div>
            </div>
          </div>
        </transition>
      </div>
    </transition>
  </Teleport>
</template>

<script>
export default {
  name: 'EmojiPickerSheet',
  
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    userReactedEmojis: {
      type: Set,
      default: () => new Set(),
    },
  },
  
  emits: ['close', 'select'],
  
  data() {
    return {
      activeCategory: 0,
      quickEmojis: ['👍', '❤️', '😂', '😮', '😢', '🙏'],
      categories: [
        {
          icon: '😀',
          name: 'Smileys',
          emojis: [
            '😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂',
            '🙂', '🙃', '😉', '😊', '😇', '🥰', '😍', '🤩',
            '😘', '😗', '😚', '😙', '🥲', '😋', '😛', '😜',
            '🤪', '😝', '🤑', '🤗', '🤭', '🤫', '🤔', '🤐',
            '🤨', '😐', '😑', '😶', '😏', '😒', '🙄', '😬',
            '🤥', '😌', '😔', '😪', '🤤', '😴', '😷', '🤒',
            '🤕', '🤢', '🤮', '🤧', '🥵', '🥶', '🥴', '😵',
            '🤯', '🤠', '🥳', '🥸', '😎', '🤓', '🧐', '😕',
            '😟', '🙁', '☹️', '😮', '😯', '😲', '😳', '🥺',
            '😦', '😧', '😨', '😰', '😥', '😢', '😭', '😱',
            '😖', '😣', '😞', '😓', '😩', '😫', '🥱', '😤',
            '😡', '😠', '🤬', '😈', '👿', '💀', '☠️', '💩',
            '🤡', '👹', '👺', '👻', '👽', '👾', '🤖',
          ],
        },
        {
          icon: '👋',
          name: 'Gestures',
          emojis: [
            '👋', '🤚', '🖐️', '✋', '🖖', '👌', '🤌', '🤏',
            '✌️', '🤞', '🤟', '🤘', '🤙', '👈', '👉', '👆',
            '🖕', '👇', '☝️', '👍', '👎', '✊', '👊', '🤛',
            '🤜', '👏', '🙌', '👐', '🤲', '🤝', '🙏', '✍️',
            '💅', '🤳', '💪', '🦾', '🦿', '🦵', '🦶', '👂',
            '🦻', '👃', '🧠', '🫀', '🫁', '🦷', '🦴', '👀',
            '👁️', '👅', '👄', '💋', '🩸',
          ],
        },
        {
          icon: '❤️',
          name: 'Hearts',
          emojis: [
            '💘', '💝', '💖', '💗', '💓', '💞', '💕', '💟',
            '❣️', '💔', '❤️‍🔥', '❤️‍🩹', '❤️', '🧡', '💛', '💚',
            '💙', '💜', '🤎', '🖤', '🤍', '💯', '💢', '💥',
            '💫', '💦', '💨', '🕳️', '💬', '👁️‍🗨️', '🗨️', '🗯️',
            '💭', '💤', '🫂', '👥', '👤', '🫡', '🫠', '🫢',
            '🫣', '🫤', '🫥', '🫦',
          ],
        },
        {
          icon: '🎉',
          name: 'Activities',
          emojis: [
            '🎉', '🎊', '🎈', '🎁', '🎀', '🎗️', '🎟️', '🎫',
            '🎖️', '🏆', '🏅', '🥇', '🥈', '🥉', '⚽', '🏀',
            '🏈', '⚾', '🥎', '🎾', '🏐', '🏉', '🥏', '🎱',
            '🪀', '🏓', '🏸', '🏒', '🏑', '🥍', '🏏', '🪃',
            '🥅', '⛳', '🪁', '🏹', '🎣', '🤿', '🥊', '🥋',
            '🎽', '🛹', '🛼', '🛷', '⛸️', '🥌', '🎿', '⛷️',
            '🏂', '🪂', '🏋️', '🤼', '🤸', '🤺', '⛹️', '🤾',
            '🏌️', '🏇', '🧘', '🏄', '🏊', '🤽', '🚣', '🧗',
            '🚴', '🚵', '🎪', '🤹', '🎭', '🎨', '🎬', '🎤',
            '🎧', '🎼', '🎹', '🥁', '🪘', '🎷', '🎺', '🪗',
            '🎸', '🪕', '🎻', '🎲', '♟️', '🎯', '🎳', '🎮',
            '🎰', '🧩',
          ],
        },
        {
          icon: '🍔',
          name: 'Food',
          emojis: [
            '🍔', '🍟', '🍕', '🌭', '🥪', '🌮', '🌯', '🫔',
            '🥙', '🧆', '🥚', '🍳', '🥘', '🍲', '🫕', '🥣',
            '🥗', '🍿', '🧈', '🧂', '🥫', '🍱', '🍘', '🍙',
            '🍚', '🍛', '🍜', '🍝', '🍠', '🍢', '🍣', '🍤',
            '🍥', '🥮', '🍡', '🥟', '🥠', '🥡', '🦀', '🦞',
            '🦐', '🦑', '🦪', '🍦', '🍧', '🍨', '🍩', '🍪',
            '🎂', '🍰', '🧁', '🥧', '🍫', '🍬', '🍭', '🍮',
            '🍯', '🍼', '🥛', '☕', '🫖', '🍵', '🧃', '🥤',
            '🧋', '🍶', '🍺', '🍻', '🥂', '🍷', '🥃', '🍸',
            '🍹', '🧉', '🍾', '🧊', '🥄', '🍴', '🍽️', '🥣',
            '🥡', '🥢',
          ],
        },
        {
          icon: '🚗',
          name: 'Travel',
          emojis: [
            '🚗', '🚕', '🚙', '🚌', '🚎', '🏎️', '🚓', '🚑',
            '🚒', '🚐', '🛻', '🚚', '🚛', '🚜', '🦯', '🦽',
            '🦼', '🛴', '🚲', '🛵', '🏍️', '🛺', '🚨', '🚔',
            '🚍', '🚘', '🚖', '🚡', '🚠', '🚟', '🚃', '🚋',
            '🚞', '🚝', '🚄', '🚅', '🚈', '🚂', '🚆', '🚇',
            '🚊', '🚉', '✈️', '🛫', '🛬', '🛩️', '💺', '🛰️',
            '🚀', '🛸', '🚁', '🛶', '⛵', '🚤', '🛥️', '🛳️',
            '⛴️', '🚢', '⚓', '🪝', '⛽', '🚧', '🚦', '🚥',
            '🚏', '🗺️', '🗿', '🗽', '🗼', '🏰', '🏯', '🏟️',
            '🎡', '🎢', '🎠', '⛲', '⛱️', '🏖️', '🏝️', '🏜️',
            '🌋', '⛰️', '🏔️', '🗻', '🏕️', '⛺', '🛖', '🏠',
            '🏡', '🏘️', '🏚️', '🏗️', '🏭', '🏢', '🏬', '🏣',
            '🏤', '🏥', '🏦', '🏨', '🏪', '🏫', '🏩', '💒',
            '🏛️', '⛪', '🕌', '🕍', '🛕', '🕋', '⛩️', '🛤️',
            '🛣️', '🗾', '🎑', '🏞️', '🌅', '🌄', '🌠', '🎇',
            '🎆', '🌇', '🌆', '🏙️', '🌃', '🌌', '🌉', '🌁',
          ],
        },
        {
          icon: '⭐',
          name: 'Symbols',
          emojis: [
            '⭐', '🌟', '✨', '💫', '⚡', '🔥', '💥', '☀️',
            '🌤️', '⛅', '🌥️', '☁️', '🌦️', '🌧️', '⛈️', '🌩️',
            '🌨️', '❄️', '☃️', '⛄', '🌬️', '💨', '🌪️', '🌫️',
            '🌈', '☔', '💧', '💦', '🌊', '✅', '☑️', '✔️',
            '❌', '❎', '➕', '➖', '➗', '✖️', '♾️', '💲',
            '💱', '™️', '©️', '®️', '〰️', '➰', '➿', '🔚',
            '🔙', '🔛', '🔝', '🔜', '✳️', '❇️', '‼️', '⁉️',
            '❓', '❔', '❕', '❗', '〽️', '⚠️', '🚸', '🔱',
            '⚜️', '🔰', '♻️', '✳️', '❇️', '🌐', '💠', 'Ⓜ️',
            '🌀', '💤', '🏧', '🚾', '♿', '🅿️', '🛗', '🈳',
            '🈂️', '🛂', '🛃', '🛄', '🛅', '🚹', '🚺', '🚼',
            '⚧️', '🚻', '🚮', '🎦', '📶', '🈁', '🔣', 'ℹ️',
            '🔤', '🔡', '🔠', '🆖', '🆗', '🆙', '🆒', '🆕',
            '🆓', '0️⃣', '1️⃣', '2️⃣', '3️⃣', '4️⃣', '5️⃣', '6️⃣',
            '7️⃣', '8️⃣', '9️⃣', '🔟', '🔢', '#️⃣', '*️⃣', '⏏️',
            '▶️', '⏸️', '⏯️', '⏹️', '⏺️', '⏭️', '⏮️', '⏩',
            '⏪', '⏫', '⏬', '◀️', '🔼', '🔽', '➡️', '⬅️',
            '⬆️', '⬇️', '↗️', '↘️', '↙️', '↖️', '↕️', '↔️',
            '↪️', '↩️', '⤴️', '⤵️', '🔀', '🔁', '🔂', '🔄',
            '🔃', '🎵', '🎶', '➕', '➖', '➗', '✖️', '🟰',
            '♠️', '♣️', '♥️', '♦️', '🃏', '🎴', '🀄',
          ],
        },
      ],
    };
  },
  
  computed: {
    currentEmojis() {
      return this.categories[this.activeCategory]?.emojis || [];
    },
  },
  
  watch: {
    visible(val) {
      if (val) {
        document.body.style.overflow = 'hidden';
        this.activeCategory = 0;
      } else {
        document.body.style.overflow = '';
      }
    },
  },
  
  methods: {
    selectEmoji(emoji) {
      this.$emit('select', emoji);
      this.close();
    },
    
    close() {
      this.$emit('close');
    },
  },
};
</script>

<style scoped>
/* Overlay */
.emoji-picker-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

/* Sheet */
.emoji-picker-sheet {
  background: var(--bs-body-bg, #fff);
  border-radius: 20px 20px 0 0;
  width: 100%;
  max-width: 500px;
  height: 65vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Header */
.picker-header {
  padding: 12px 16px 8px;
  position: relative;
  flex-shrink: 0;
}

.picker-handle {
  width: 40px;
  height: 4px;
  background: var(--bs-secondary-bg, #e9ecef);
  border-radius: 2px;
  margin: 0 auto 12px;
}

.picker-title {
  text-align: center;
  font-size: 18px;
  font-weight: 600;
  color: var(--bs-body-color, #333);
}

.picker-close {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 32px;
  height: 32px;
  border: none;
  background: var(--bs-tertiary-bg, #f5f5f5);
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--bs-secondary-color, #6c757d);
  transition: all 0.2s;
}

.picker-close:hover {
  background: var(--bs-danger, #dc3545);
  color: #fff;
}

/* Quick Reactions */
.quick-reactions {
  display: flex;
  justify-content: center;
  gap: 8px;
  padding: 12px 16px;
  border-bottom: 1px solid var(--bs-border-color, #e5e5e5);
  flex-shrink: 0;
}

.quick-emoji {
  width: 48px;
  height: 48px;
  border: 2px solid transparent;
  background: var(--bs-tertiary-bg, #f5f5f5);
  border-radius: 50%;
  cursor: pointer;
  font-size: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.quick-emoji:hover {
  background: var(--bs-success-bg-subtle, #d1e7dd);
  transform: scale(1.1);
}

.quick-emoji.is-reacted {
  border-color: var(--bs-success, #198754);
  background: var(--bs-success-bg-subtle, #d1e7dd);
}

/* Category Tabs */
.category-tabs {
  display: flex;
  padding: 8px 16px;
  gap: 4px;
  border-bottom: 1px solid var(--bs-border-color, #e5e5e5);
  overflow-x: auto;
  flex-shrink: 0;
}

.category-tabs::-webkit-scrollbar {
  display: none;
}

.category-tab {
  flex: 1;
  min-width: 44px;
  height: 40px;
  border: none;
  background: transparent;
  border-radius: 8px;
  cursor: pointer;
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.category-tab:hover {
  background: var(--bs-tertiary-bg, #f5f5f5);
}

.category-tab.active {
  background: var(--bs-success-bg-subtle, #d1e7dd);
}

/* Emoji Grid */
.emoji-grid-container {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
}

.emoji-grid {
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  gap: 4px;
}

.emoji-item {
  aspect-ratio: 1;
  border: none;
  background: transparent;
  border-radius: 8px;
  cursor: pointer;
  font-size: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
}

.emoji-item:hover {
  background: var(--bs-tertiary-bg, #f5f5f5);
  transform: scale(1.15);
}

.emoji-item.is-reacted {
  background: var(--bs-success-bg-subtle, #d1e7dd);
}

/* Transitions */
.picker-fade-enter-active,
.picker-fade-leave-active {
  transition: opacity 0.3s ease;
}

.picker-fade-enter-from,
.picker-fade-leave-to {
  opacity: 0;
}

.picker-slide-enter-active,
.picker-slide-leave-active {
  transition: transform 0.3s ease;
}

.picker-slide-enter-from,
.picker-slide-leave-to {
  transform: translateY(100%);
}

/* Dark mode */
[data-bs-theme="dark"] .emoji-picker-sheet {
  background: var(--bs-body-bg);
}

/* Responsive */
@media (max-width: 480px) {
  .emoji-grid {
    grid-template-columns: repeat(7, 1fr);
  }
  
  .emoji-item {
    font-size: 22px;
  }
  
  .quick-emoji {
    width: 42px;
    height: 42px;
    font-size: 20px;
  }
}

@media (min-width: 768px) {
  .emoji-picker-overlay {
    align-items: center;
  }
  
  .emoji-picker-sheet {
    border-radius: 20px;
    height: 60vh;
    max-height: 600px;
  }
}
</style>
